import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import toast from '../../utils/toast';

const CustomPageForm = ({ page, onSubmit, onCancel }) => {
  const { checkPermission } = useAuth();
  const [loading, setLoading] = useState(false);
  const [verificationTypes, setVerificationTypes] = useState([]);
  const [documentVerifications, setDocumentVerifications] = useState([]);
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [groupSearchTerm, setGroupSearchTerm] = useState('');
  const [documentSearchTerm, setDocumentSearchTerm] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showGroupDropdown, setShowGroupDropdown] = useState(false);
  const [showDocumentDropdown, setShowDocumentDropdown] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    menuName: '',
    menuCategory: '',
    description: '',
    fields: [],
    verificationTypes: [],
    documentVerifications: [],
    accessUsers: [],
    accessGroups: [],
    formSubmissionAction: 'save',
    invoiceSettings: {
      enabled: false,
      fixedValue: 0,
      description: '',
      formTokens: []
    },
    taskSettings: {
      taskNameField: '',
      taskDescriptionField: '',
      deadlineField: '',
      defaultDeadline: {
        days: 0,
        hours: 0
      }
    }
  });

  const fieldTypes = [
    { value: 'text', label: 'Text' },
    { value: 'number', label: 'Number' },
    { value: 'date', label: 'Date' },
    { value: 'dropdown', label: 'Dropdown' },
    { value: 'multitext', label: 'Multi-line Text' },
    { value: 'upload', label: 'File Upload' },
    { value: 'masterdata', label: 'Master Data Selection' },
    { value: 'user', label: 'User Selection' }
  ];

  useEffect(() => {
    fetchVerificationTypes();
    fetchDocumentVerifications();
    fetchUsers();
    fetchGroups();
    if (page) {
      setFormData({
        name: page.name || '',
        menuName: page.menuName || '',
        menuCategory: page.menuCategory || '',
        description: page.description || '',
        fields: page.fields || [],
        verificationTypes: page.verificationTypes || [],
        documentVerifications: page.documentVerifications || [],
        accessUsers: page.accessUsers || [],
        accessGroups: page.accessGroups || [],
        formSubmissionAction: page.formSubmissionAction || 'save',
        invoiceSettings: page.invoiceSettings || {
          enabled: false,
          fixedValue: 0,
          description: '',
          formTokens: []
        },
        taskSettings: page.taskSettings || {
          taskNameField: '',
          taskDescriptionField: '',
          deadlineField: '',
          defaultDeadline: {
            days: 0,
            hours: 0
          }
        }
      });
    }
  }, [page]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.user-dropdown') && !event.target.closest('.group-dropdown') && !event.target.closest('.document-dropdown')) {
        setShowUserDropdown(false);
        setShowGroupDropdown(false);
        setShowDocumentDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchVerificationTypes = async () => {
    try {
      const response = await api.get('/verification-types');
      if (response.data.success) {
        setVerificationTypes(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching verification types:', error);
    }
  };

  const fetchDocumentVerifications = async () => {
    try {
      const response = await api.get('/custom-documents');
      if (response.data.success) {
        console.log("Checklist", response.data.data.docs);
        setDocumentVerifications(response.data.data.docs || response.data.data);
      }
    } catch (error) {
      console.error('Error fetching Checklist:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      if (response.data.success) {
        setUsers(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchGroups = async () => {
    try {
      const response = await api.get('/groups');
      console.log('Groups API response:', response.data);
      setGroups(response.data);
      console.log('Groups state set:', response.data);
    } catch (error) {
      console.error('Error fetching groups:', error);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNestedChange = (parent, field, value) => {
    setFormData(prev => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [field]: value
      }
    }));
  };

  const handleFieldChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      fields: prev.fields.map((f, i) => 
        i === index ? { ...f, [field]: value } : f
      )
    }));
  };

  const addField = () => {
    const newField = {
      name: '',
      label: '',
      type: 'text',
      required: false,
      options: [],
      placeholder: '',
      validation: {},
      isDeadlineField: false,
      showInTable: true,
      order: formData.fields.length + 1
    };

    setFormData(prev => ({
      ...prev,
      fields: [...prev.fields, newField]
    }));
  };

  const removeField = (index) => {
    setFormData(prev => ({
      ...prev,
      fields: prev.fields.filter((_, i) => i !== index)
    }));
  };

  const addOption = (fieldIndex) => {
    setFormData(prev => ({
      ...prev,
      fields: prev.fields.map((field, i) => 
        i === fieldIndex 
          ? { ...field, options: [...field.options, ''] }
          : field
      )
    }));
  };

  const removeOption = (fieldIndex, optionIndex) => {
    setFormData(prev => ({
      ...prev,
      fields: prev.fields.map((field, i) => 
        i === fieldIndex 
          ? { ...field, options: field.options.filter((_, j) => j !== optionIndex) }
          : field
      )
    }));
  };

  const handleOptionChange = (fieldIndex, optionIndex, value) => {
    setFormData(prev => ({
      ...prev,
      fields: prev.fields.map((field, i) => 
        i === fieldIndex 
          ? { 
              ...field, 
              options: field.options.map((opt, j) => 
                j === optionIndex ? value : opt
              )
            }
          : field
      )
    }));
  };

  const filteredUsers = users.filter(user => 
    !formData.accessUsers.includes(user._id) &&
    (user.firstName?.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
     user.lastName?.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
     user.email?.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
     user.username?.toLowerCase().includes(userSearchTerm.toLowerCase()))
  );

  const filteredGroups = groups.filter(group => 
    !formData.accessGroups.includes(group._id) &&
    group.name?.toLowerCase().includes(groupSearchTerm.toLowerCase())
  );

  const filteredDocuments = documentVerifications.filter(doc => 
    !formData.documentVerifications.includes(doc._id) &&
    doc.name?.toLowerCase().includes(documentSearchTerm.toLowerCase())
  );

  const handleAddUser = (userId) => {
    if (!formData.accessUsers.includes(userId)) {
      setFormData(prev => ({
        ...prev,
        accessUsers: [...prev.accessUsers, userId]
      }));
    }
    setUserSearchTerm('');
    setShowUserDropdown(false);
  };

  const handleAddGroup = (groupId) => {
    if (!formData.accessGroups.includes(groupId)) {
      setFormData(prev => ({
        ...prev,
        accessGroups: [...prev.accessGroups, groupId]
      }));
    }
    setGroupSearchTerm('');
    setShowGroupDropdown(false);
  };

  const handleRemoveUser = (userId) => {
    setFormData(prev => ({
      ...prev,
      accessUsers: prev.accessUsers.filter(id => id !== userId)
    }));
  };

  const handleRemoveGroup = (groupId) => {
    setFormData(prev => ({
      ...prev,
      accessGroups: prev.accessGroups.filter(id => id !== groupId)
    }));
  };

  const handleClearAllUsers = () => {
    setFormData(prev => ({
      ...prev,
      accessUsers: []
    }));
  };

  const handleClearAllGroups = () => {
    setFormData(prev => ({
      ...prev,
      accessGroups: []
    }));
  };

  const handleAddDocument = (documentId) => {
    console.log("Handle add document", documentId);
    if (!formData.documentVerifications.includes(documentId)) {
      setFormData(prev => ({
        ...prev,
        documentVerifications: [...prev.documentVerifications, documentId]
      }));
    }
    setDocumentSearchTerm('');
    setShowDocumentDropdown(false);
  };

  const handleRemoveDocument = (documentId) => {
    setFormData(prev => ({
      ...prev,
      documentVerifications: prev.documentVerifications.filter(id => id !== documentId)
    }));
  };

  const handleClearAllDocuments = () => {
    setFormData(prev => ({
      ...prev,
      documentVerifications: []
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name || !formData.menuName || !formData.menuCategory) {
      toast.error('Name, menu name, and menu category are required');
      return;
    }

    if (formData.fields.length === 0) {
      toast.error('At least one field is required');
      return;
    }

    // Validate field names are unique
    const fieldNames = formData.fields.map(f => f.name);
    const uniqueFieldNames = new Set(fieldNames);
    if (fieldNames.length !== uniqueFieldNames.size) {
      toast.error('Field names must be unique');
      return;
    }

    // Validate required fields have names and labels
    for (const field of formData.fields) {
      if (!field.name || !field.label) {
        toast.error('All fields must have a name and label');
        return;
      }
    }

    // Validate deadline field exists if task creation is enabled
    if (formData.formSubmissionAction === 'create_task' && formData.taskSettings.deadlineField) {
      const deadlineFieldExists = formData.fields.some(f => f.name === formData.taskSettings.deadlineField);
      if (!deadlineFieldExists) {
        toast.error('Deadline field must exist in the form fields');
        return;
      }
    }

    setLoading(true);
    try {
      await onSubmit(formData);
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold">
            {page ? 'Edit Custom Page' : 'Create Custom Page'}
          </h2>
          <button
            onClick={onCancel}
            className="text-gray-500 hover:text-gray-700"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Page Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter page name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Menu Name *
              </label>
              <input
                type="text"
                value={formData.menuName}
                onChange={(e) => handleInputChange('menuName', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter menu name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Menu Category *
              </label>
              <input
                type="text"
                value={formData.menuCategory}
                onChange={(e) => handleInputChange('menuCategory', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter menu category"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <input
                type="text"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter description"
              />
            </div>
          </div>

          {/* Form Submission Action */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Form Submission Action
            </label>
            <select
              value={formData.formSubmissionAction}
              onChange={(e) => handleInputChange('formSubmissionAction', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="save">Save Only</option>
              <option value="create_task">Create Task</option>
              <option value="create_invoice">Create Invoice</option>
            </select>
          </div>

          {/* Task Settings */}
          {formData.formSubmissionAction === 'create_task' && (
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="text-lg font-medium mb-4">Task Settings</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Task Name Field
                  </label>
                  <select
                    value={formData.taskSettings.taskNameField}
                    onChange={(e) => handleNestedChange('taskSettings', 'taskNameField', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select a field</option>
                    {formData.fields.map((field, index) => (
                      <option key={index} value={field.name}>{field.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Task Description Field
                  </label>
                  <select
                    value={formData.taskSettings.taskDescriptionField}
                    onChange={(e) => handleNestedChange('taskSettings', 'taskDescriptionField', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select a field</option>
                    {formData.fields.map((field, index) => (
                      <option key={index} value={field.name}>{field.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Deadline Field
                  </label>
                  <select
                    value={formData.taskSettings.deadlineField}
                    onChange={(e) => handleNestedChange('taskSettings', 'deadlineField', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select a field</option>
                    {formData.fields.filter(f => f.type === 'date').map((field, index) => (
                      <option key={index} value={field.name}>{field.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Default Deadline (Days)
                  </label>
                  <input
                    type="number"
                    value={formData.taskSettings.defaultDeadline.days}
                    onChange={(e) => handleNestedChange('taskSettings', 'defaultDeadline', {
                      ...formData.taskSettings.defaultDeadline,
                      days: parseInt(e.target.value) || 0
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="0"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Invoice Settings */}
          {formData.formSubmissionAction === 'create_invoice' && (
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="text-lg font-medium mb-4">Invoice Settings</h3>
              <div className="space-y-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.invoiceSettings.enabled}
                    onChange={(e) => handleNestedChange('invoiceSettings', 'enabled', e.target.checked)}
                    className="mr-2"
                  />
                  <label className="text-sm font-medium text-gray-700">
                    Enable Invoice Creation
                  </label>
                </div>
                {formData.invoiceSettings.enabled && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Fixed Value
                      </label>
                      <input
                        type="number"
                        value={formData.invoiceSettings.fixedValue}
                        onChange={(e) => handleNestedChange('invoiceSettings', 'fixedValue', parseFloat(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        min="0"
                        step="0.01"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description
                      </label>
                      <input
                        type="text"
                        value={formData.invoiceSettings.description}
                        onChange={(e) => handleNestedChange('invoiceSettings', 'description', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Invoice description"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Fields */}
          <div>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium">Form Fields</h3>
              <button
                type="button"
                onClick={addField}
                className="bg-blue-600 text-white px-3 py-1 rounded-md hover:bg-blue-700 transition-colors"
              >
                Add Field
              </button>
            </div>
            
            <div className="space-y-4">
              {formData.fields.map((field, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-4">
                    <h4 className="font-medium">Field {index + 1}</h4>
                    <button
                      type="button"
                      onClick={() => removeField(index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      Remove
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Field Name *
                      </label>
                      <input
                        type="text"
                        value={field.name}
                        onChange={(e) => handleFieldChange(index, 'name', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., client_name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Field Label *
                      </label>
                      <input
                        type="text"
                        value={field.label}
                        onChange={(e) => handleFieldChange(index, 'label', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., Client Name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Field Type *
                      </label>
                      <select
                        value={field.type}
                        onChange={(e) => handleFieldChange(index, 'type', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        {fieldTypes.map(type => (
                          <option key={type.value} value={type.value}>{type.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Placeholder
                      </label>
                      <input
                        type="text"
                        value={field.placeholder}
                        onChange={(e) => handleFieldChange(index, 'placeholder', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter placeholder text"
                      />
                    </div>
                  </div>
                  
                  <div className="mt-4 space-y-2">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={field.required}
                        onChange={(e) => handleFieldChange(index, 'required', e.target.checked)}
                        className="mr-2"
                      />
                      <label className="text-sm font-medium text-gray-700">
                        Required Field
                      </label>
                    </div>
                    
                    {field.type === 'date' && (
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={field.isDeadlineField}
                          onChange={(e) => handleFieldChange(index, 'isDeadlineField', e.target.checked)}
                          className="mr-2"
                        />
                        <label className="text-sm font-medium text-gray-700">
                          Use as Deadline Field
                        </label>
                      </div>
                    )}
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={field.showInTable}
                        onChange={(e) => handleFieldChange(index, 'showInTable', e.target.checked)}
                        className="mr-2"
                      />
                      <label className="text-sm font-medium text-gray-700">
                        Show in Table
                      </label>
                    </div>
                  </div>
                  
                  {/* Dropdown Options */}
                  {field.type === 'dropdown' && (
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Dropdown Options
                      </label>
                      <div className="space-y-2">
                        {(field.options || []).map((option, optionIndex) => (
                          <div key={optionIndex} className="flex gap-2">
                            <input
                              type="text"
                              value={option}
                              onChange={(e) => handleOptionChange(index, optionIndex, e.target.value)}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="Option text"
                            />
                            <button
                              type="button"
                              onClick={() => removeOption(index, optionIndex)}
                              className="text-red-600 hover:text-red-800 px-2"
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={() => addOption(index)}
                          className="text-blue-600 hover:text-blue-800 text-sm"
                        >
                          + Add Option
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Verification Types */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Verification Types
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {verificationTypes.map((vt) => (
                <div key={vt._id} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.verificationTypes.includes(vt._id)}
                    onChange={(e) => {
                      const newTypes = e.target.checked
                        ? [...formData.verificationTypes, vt._id]
                        : formData.verificationTypes.filter(id => id !== vt._id);
                      handleInputChange('verificationTypes', newTypes);
                    }}
                    className="mr-2"
                  />
                  <label className="text-sm text-gray-700">{vt.name}</label>
                </div>
              ))}
            </div>
          </div>

          {/* Checklist */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-medium text-gray-700">Checklist</label>
              {formData.documentVerifications.length > 0 && (
                <button
                  type="button"
                  onClick={handleClearAllDocuments}
                  className="text-xs text-red-600 hover:text-red-800"
                >
                  Clear All
                </button>
              )}
            </div>
            
            {/* Selected Documents Tags */}
            {formData.documentVerifications.length > 0 && (
              <div className="mb-3 flex flex-wrap gap-2">
                {formData.documentVerifications.map(documentId => {
                  const document = documentVerifications.find(d => d._id === documentId._id);
                  return document ? (
                    <span
                      key={documentId}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800"
                    >
                      {document.name}
                      <button
                        type="button"
                        onClick={() => handleRemoveDocument(documentId)}
                        className="ml-2 text-purple-600 hover:text-purple-800"
                      >
                        ✕
                      </button>
                    </span>
                  ) : null;
                })}
              </div>
            )}

            {/* Search and Add Documents */}
            <div className="relative document-dropdown">
              <input
                type="text"
                placeholder="Search documents to add..."
                value={documentSearchTerm}
                onChange={(e) => {
                  setDocumentSearchTerm(e.target.value);
                  setShowDocumentDropdown(true);
                }}
                onFocus={() => setShowDocumentDropdown(true)}
                className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
              />
              
              {/* Document Dropdown */}
              {showDocumentDropdown && documentSearchTerm && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                  {filteredDocuments.length === 0 ? (
                    <div className="p-3 text-sm text-gray-500">
                      No documents found matching "{documentSearchTerm}"
                    </div>
                  ) : (
                    <div className="py-1">
                      {filteredDocuments.map((document) => (
                        <button
                          key={document._id}
                          onClick={() => handleAddDocument(document)}
                          className="w-full text-left px-3 py-2 hover:bg-gray-100 focus:outline-none focus:bg-gray-100 text-sm"
                        >
                          <div className="font-medium text-gray-900">
                            {document.name}
                          </div>
                          {document.description && (
                            <div className="text-gray-500">{document.description}</div>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Access Control */}
          <div>
            <h3 className="text-lg font-medium mb-4">Access Control</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700">Users</label>
                  {formData.accessUsers.length > 0 && (
                    <button
                      type="button"
                      onClick={handleClearAllUsers}
                      className="text-xs text-red-600 hover:text-red-800"
                    >
                      Clear All
                    </button>
                  )}
                </div>
                
                {/* Selected Users Tags */}
                {formData.accessUsers.length > 0 && (
                  <div className="mb-3 flex flex-wrap gap-2">
                    {formData.accessUsers.map(userId => {
                      const user = users.find(u => u._id === userId);
                      return user ? (
                        <span
                          key={userId}
                          className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
                        >
                          {user.firstName} {user.lastName}
                          <button
                            type="button"
                            onClick={() => handleRemoveUser(userId)}
                            className="ml-2 text-blue-600 hover:text-blue-800"
                          >
                            ✕
                          </button>
                        </span>
                      ) : null;
                    })}
                  </div>
                )}

                {/* Search and Add Users */}
                <div className="relative user-dropdown">
                  <input
                    type="text"
                    placeholder="Search users to add..."
                    value={userSearchTerm}
                    onChange={(e) => {
                      setUserSearchTerm(e.target.value);
                      setShowUserDropdown(true);
                    }}
                    onFocus={() => setShowUserDropdown(true)}
                    className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                  />
                  
                  {/* User Dropdown */}
                  {showUserDropdown && userSearchTerm && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                      {filteredUsers.length === 0 ? (
                        <div className="p-3 text-sm text-gray-500">
                          No users found matching "{userSearchTerm}"
                        </div>
                      ) : (
                        <div className="py-1">
                          {filteredUsers.map((user) => (
                            <button
                              key={user._id}
                              onClick={() => handleAddUser(user._id)}
                              className="w-full text-left px-3 py-2 hover:bg-gray-100 focus:outline-none focus:bg-gray-100 text-sm"
                            >
                              <div className="font-medium text-gray-900">
                                {user.firstName} {user.lastName}
                              </div>
                              <div className="text-gray-500">{user.email}</div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700">Groups</label>
                  {formData.accessGroups.length > 0 && (
                    <button
                      type="button"
                      onClick={handleClearAllGroups}
                      className="text-xs text-red-600 hover:text-red-800"
                    >
                      Clear All
                    </button>
                  )}
                </div>
                
                {/* Selected Groups Tags */}
                {formData.accessGroups.length > 0 && (
                  <div className="mb-3 flex flex-wrap gap-2">
                    {formData.accessGroups.map(groupId => {
                      const group = groups.find(g => g._id === groupId);
                      return group ? (
                        <span
                          key={groupId}
                          className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800"
                        >
                          {group.name}
                          <button
                            type="button"
                            onClick={() => handleRemoveGroup(groupId)}
                            className="ml-2 text-green-600 hover:text-green-800"
                          >
                            ✕
                          </button>
                        </span>
                      ) : null;
                    })}
                  </div>
                )}

                {/* Search and Add Groups */}
                <div className="relative group-dropdown">
                  <input
                    type="text"
                    placeholder="Search groups to add..."
                    value={groupSearchTerm}
                    onChange={(e) => {
                      setGroupSearchTerm(e.target.value);
                      setShowGroupDropdown(true);
                    }}
                    onFocus={() => setShowGroupDropdown(true)}
                    className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                  />
                  
                  {/* Group Dropdown */}
                  {showGroupDropdown && groupSearchTerm && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                      {filteredGroups.length === 0 ? (
                        <div className="p-3 text-sm text-gray-500">
                          No groups found matching "{groupSearchTerm}"
                        </div>
                      ) : (
                        <div className="py-1">
                          {filteredGroups.map((group) => (
                            <button
                              key={group._id}
                              onClick={() => handleAddGroup(group._id)}
                              className="w-full text-left px-3 py-2 hover:bg-gray-100 focus:outline-none focus:bg-gray-100 text-sm"
                            >
                              <div className="font-medium text-gray-900">
                                {group.name}
                              </div>
                              {group.description && (
                                <div className="text-gray-500">{group.description}</div>
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-4 pt-6 border-t">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {loading ? 'Saving...' : (page ? 'Update Page' : 'Create Page')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CustomPageForm; 